var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2020, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, } from 'react';
import { css } from '@emotion/react';
import { focusVisible } from '../../styles/style-mixins';
import { Body } from '../Body/Body';
import { useComponents } from '../ComponentsContext';
import { useClickEvent } from '../../hooks/useClickEvent';
const anchorStyles = (theme) => css `
  display: inline-block;
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  border: 0;
  outline: none;
  background: none;
  padding: 0;
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
  color: var(--cui-fg-accent);
  border-radius: ${theme.borderRadius.byte};
  transition: opacity ${theme.transitions.default},
    color ${theme.transitions.default},
    background-color ${theme.transitions.default},
    border-color ${theme.transitions.default};

  &:hover {
    color: var(--cui-fg-accent-hovered);
    cursor: pointer;
  }

  &:active {
    color: var(--cui-fg-accent-pressed);
  }

  ${focusVisible()};
`;
/**
 * The Anchor is used to display a link or button that visually looks like
 * a hyperlink. Based on the Body component, so it also supports its props.
 */
export const Anchor = forwardRef((_a, ref) => {
    var { tracking } = _a, props = __rest(_a, ["tracking"]);
    const components = useComponents();
    const Link = components.Link;
    const handleClick = useClickEvent(props.onClick, tracking, 'anchor');
    if (!props.href && !props.onClick) {
        return _jsx(Body, Object.assign({ as: "span" }, props, { ref: ref }));
    }
    if (props.href) {
        return (_jsx(Body, Object.assign({}, props, { css: anchorStyles, as: Link, ref: ref, onClick: handleClick })));
    }
    return (_jsx(Body, Object.assign({ as: "button" }, props, { css: anchorStyles, ref: ref, onClick: handleClick })));
});
Anchor.displayName = 'Anchor';
